import { getReaderChapterVoice, type ReaderChapter } from '@/services/booksApi';
import { hasAsset } from '@/native/assetStore';
import { isNativeApp } from '@/utils/platform';
import { useNetworkState } from './useNetworkState';

/**
 * The one sentence the offline feature ever says.
 *
 * There is no depth indicator by design: the buffer either works and there is
 * nothing to look at, or the listener has run past what is on disk. This is
 * what they see then, and it has to carry the whole story — the text is still
 * here, the narration is not, and nothing needs doing.
 */
export const OFFLINE_BOUNDARY_MESSAGE =
  "You've reached the end of the downloaded audio. You can keep reading, and narration resumes when you're back online.";

interface UseOfflineBoundaryArgs {
  chapter: ReaderChapter | null;
  voiceKey: string;
}

/**
 * True when the listener is offline on a chapter whose narration is not on
 * this device — the buffer edge. Only ever true in the native app; a chapter
 * with no narration in this voice is read, not played, and is never "past
 * the edge".
 */
export const useOfflineBoundary = ({ chapter, voiceKey }: UseOfflineBoundaryArgs): boolean => {
  const { isOffline } = useNetworkState();
  if (!isNativeApp() || !isOffline || !chapter) {
    return false;
  }
  const voice = getReaderChapterVoice(chapter, voiceKey);
  if (!voice) {
    return false;
  }
  return !hasAsset(voice.audioUrl);
};
